import { z } from "zod";
import {
    AGENT_SKILL_BODY_MAX_CHARS,
    AGENT_SKILL_DESCRIPTION_MAX_CHARS,
    AGENT_SKILL_KICK_BODY_MAX_CHARS,
    AGENT_SKILL_KICK_DESCRIPTION_MAX_CHARS,
    AGENT_SKILL_TITLE_MAX_CHARS,
} from "@common/constants/agentSkill";
import type {
    AgentSkillCreateBody,
    AgentSkillUpdateBody,
} from "@common/schemas/domains/agentSkills/agent-skill.schema";
import {
    AgentSkillCategory,
    AgentSkillKind,
    AgentSkillLoadMode,
} from "@common/types/agentSkill";

/** The tool a `tool_not_attached:<name>` save warning names, else null. */
export const toolNotAttached = (warning: string): string | null =>
    warning.startsWith("tool_not_attached:")
        ? warning.slice("tool_not_attached:".length)
        : null;

export const agentSkillDescriptionMaxChars = (kind: AgentSkillKind): number =>
    kind === AgentSkillKind.KICK
        ? AGENT_SKILL_KICK_DESCRIPTION_MAX_CHARS
        : AGENT_SKILL_DESCRIPTION_MAX_CHARS;

export const agentSkillBodyMaxChars = (kind: AgentSkillKind): number =>
    kind === AgentSkillKind.KICK
        ? AGENT_SKILL_KICK_BODY_MAX_CHARS
        : AGENT_SKILL_BODY_MAX_CHARS;

export const agentSkillFormSchemaFor = (kind: AgentSkillKind) => {
    const bodyMaxChars = agentSkillBodyMaxChars(kind);
    const descriptionMaxChars = agentSkillDescriptionMaxChars(kind);
    return z.object({
        title: z
            .string()
            .trim()
            .min(1, "Enter a title")
            .max(
                AGENT_SKILL_TITLE_MAX_CHARS,
                `Keep the title under ${AGENT_SKILL_TITLE_MAX_CHARS} characters`,
            ),
        description: z
            .string()
            .trim()
            .min(1, "Enter a one-line description")
            .max(
                descriptionMaxChars,
                `Keep the description under ${descriptionMaxChars} characters`,
            ),
        category: z.nativeEnum(AgentSkillCategory),
        loadMode: z.nativeEnum(AgentSkillLoadMode),
        clientWorkspaceId: z.string().nullable(),
        body: z
            .string()
            .min(1, "Write the skill text")
            .max(
                bodyMaxChars,
                `Keep the skill text under ${bodyMaxChars.toLocaleString("en-US")} characters`,
            ),
    });
};

export const agentSkillFormSchema = agentSkillFormSchemaFor(
    AgentSkillKind.FIRM,
);

export type AgentSkillFormValues = z.infer<typeof agentSkillFormSchema>;

export const emptyAgentSkillForm = (
    clientWorkspaceId: string | null,
): AgentSkillFormValues => ({
    title: "",
    description: "",
    category: AgentSkillCategory.BOOKKEEPING,
    loadMode: AgentSkillLoadMode.ON_DEMAND,
    clientWorkspaceId,
    body: "",
});

interface EditableAgentSkill {
    title: string;
    description: string;
    category: AgentSkillCategory;
    loadMode: AgentSkillLoadMode;
    workspaceId: string | null;
    body: string;
}

export const getEditAgentSkillForm = (
    skill: EditableAgentSkill,
): AgentSkillFormValues => ({
    title: skill.title,
    description: skill.description,
    category: skill.category,
    loadMode: skill.loadMode,
    clientWorkspaceId: skill.workspaceId,
    body: skill.body,
});

export const buildCreateAgentSkillRequest = (
    values: AgentSkillFormValues,
): AgentSkillCreateBody => ({
    title: values.title.trim(),
    description: values.description.trim(),
    category: values.category,
    loadMode: values.loadMode,
    body: values.body,
    clientWorkspaceId: values.clientWorkspaceId,
});

// Category and frequency are Kick-owned on a Kick skill, so an update of one
// never sends them.
export const buildUpdateAgentSkillRequest = ({
    values,
    initialValues,
    kind = AgentSkillKind.FIRM,
}: {
    values: AgentSkillFormValues;
    initialValues: AgentSkillFormValues;
    kind?: AgentSkillKind;
}): AgentSkillUpdateBody => {
    const next = buildCreateAgentSkillRequest(values);
    const previous = buildCreateAgentSkillRequest(initialValues);
    const isKick = kind === AgentSkillKind.KICK;
    return {
        ...(next.title !== previous.title ? { title: next.title } : {}),
        ...(next.description !== previous.description
            ? { description: next.description }
            : {}),
        ...(!isKick && next.category !== previous.category
            ? { category: next.category }
            : {}),
        ...(!isKick && next.loadMode !== previous.loadMode
            ? { loadMode: next.loadMode }
            : {}),
        ...(next.body !== previous.body ? { body: next.body } : {}),
    };
};

export interface DirtyRevision {
    skillId: string;
    revisionId: string | null;
}

// The revision an editing session stays on once the member has typed. The
// form reports its dirty state again whenever the callback changes, so an
// existing pin on the same skill is kept, not moved to the latest revision.
export const pinDirtyRevision = (
    current: DirtyRevision | null,
    {
        isDirty,
        skillId,
        revisionId,
    }: { isDirty: boolean; skillId: string; revisionId: string | null },
): DirtyRevision | null => {
    if (!isDirty) {
        return null;
    }
    return current?.skillId === skillId ? current : { skillId, revisionId };
};
